// 📁 src/Products.js

import React, { useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import ProductCard from './components/ProductCard';
import PromoBanner from './components/Promo/PromoBanner';
import atsImg from './images/ats.png';
import scoutImg from './images/scout.png';
import hrisImg from './images/hris.png';
import auditreadyImg from './images/auditready.png';
import performedgeImg from './images/performedge.png';
import heroImg from './images/hero-hr.png';

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export default function Products() {
  const navigate = useNavigate();
  const { currency, billingCycle } = useOutletContext();

  const sections = [
    {
      id: 'ATS',
      title: 'ATS — Applicant Tracking System',
      tagline: 'Streamline hiring with AI-powered precision',
      overview:
        'Talentraxion ATS transforms recruitment into a seamless, data-driven experience...',
      features: [
        'AI Shortlisting Engine',
        'Dynamic Recruitment Funnel',
        'Auto & Manual Email Triggers',
        'Custom Hiring Pipelines',
        'Interview Scheduling & Feedback',
        'Dashboard for HR Managers'
      ],
      message: 'Save time, reduce hiring costs, and build a reputation candidates admire.',
      image: atsImg
    },
    {
      id: 'Scout',
      title: 'Scout — Talent Discovery Engine',
      tagline: 'Find hidden gems before your competitors do',
      overview: 'Scout is your AI-powered sourcing assistant...',
      features: [
        'AI Talent Matching',
        'Boolean-Free Search',
        'Diversity Filters',
        'Talent Pools',
        'Chrome Extension',
        'Auto Outreach Triggers'
      ],
      message: 'Build a future-ready workforce...',
      image: scoutImg
    },
    {
      id: 'PeopleSync',
      title: 'HRIS — Human Resource Information System',
      tagline: 'Empower your workforce with clarity and control',
      overview: 'A centralized hub for all employee data...',
      features: [
        'Employee profiles and history',
        'Leave & attendance management',
        'Org charts and directory',
        'Payroll integration',
        'Employee self-service portal'
      ],
      message: 'Give your team the tools they deserve...',
      image: hrisImg
    },
    {
      id: 'AuditReady',
      title: 'AuditReady Suite — Compliance & Governance',
      tagline: 'Smart compliance, simplified',
      overview: 'Stay audit-ready with automated document tracking...',
      features: [
        'Digital Document Vault',
        'Policy acknowledgment tracking',
        'Immutable audit trails',
        'Compliance calendar',
        'Multi-territory rules engine'
      ],
      message: 'Protect your people and reputation...',
      image: auditreadyImg
    },
    {
      id: 'PerformEdge',
      title: 'PerformEdge — Performance Management',
      tagline: 'Track growth. Reward excellence.',
      overview: 'PerformEdge helps you build a culture of continuous feedback...',
      features: [
        'Goal setting and tracking',
        '1:1 meeting logs',
        '360° feedback',
        'Review cycles',
        'Recognition tools'
      ],
      message: 'Turn feedback into fuel for growth.',
      image: performedgeImg
    }
  ];

  const handleSubscribe = (moduleId) => {
    navigate(`/register?module=${moduleId}`);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-fade-in-up');
        });
      },
      { threshold: 0.2 }
    );

    document.querySelectorAll('.product-section').forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hash = window.location.hash?.substring(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#f8fafc] text-[#1e293b] px-4 sm:px-6 py-12 space-y-16">
      {/* ✅ Occasion-Based Promo Banner */}
      <PromoBanner />

      {/* ✅ Roman Banner — Always Visible */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[1001] bg-gradient-to-r from-orange-500 to-yellow-400 px-6 py-2 shadow-lg shadow-orange-300/40 text-sm font-semibold animate-banner tracking-wide rounded-full w-fit max-w-md text-center">
        Save time, save money
      </div>
      {console.log('✅ Roman Banner mounted')}

      {/* ✅ Hero Section */}
      <section className="relative bg-gradient-to-r from-indigo-100 to-blue-50 rounded-xl shadow-md p-8 flex flex-col md:flex-row items-center justify-between mt-12">
        <div className="space-y-4 md:w-2/3 text-center md:text-left">
          <h1 className="heading1">AI-Powered HR Solutions for Every Team</h1>
          <p className="para1">We build elegantly simple HR software for ambitious teams...</p>
          <button
            onClick={() => {
              try {
                scrollToId('ATS');
              } catch {
                navigate('/products#ATS');
              }
            }}
            className="bg-indigo-600 text-white px-6 py-3 rounded-full shadow hover:bg-indigo-700 transition"
          >
            Explore Products
          </button>
        </div>
        <div className="md:w-1/3 mt-6 md:mt-0">
          <img src={heroImg} alt="HR Dashboard" className="w-full max-w-sm rounded-lg shadow-lg" />
        </div>
      </section>

      {/* ✅ Product Cards */}
      {sections.map((section, index) => (
        <section key={section.id} id={section.id} className="product-section">
          <ProductCard
            {...section}
            index={index}
            currency={currency}
            billingCycle={billingCycle}
            discountPercent={15}
            onSubscribe={handleSubscribe}
          />
        </section>
      ))}

      {/* ✅ CTA */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => navigate('/register')}
          className="bg-indigo-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-indigo-700 transition animate-pulse"
        >
          Request Demo
        </button>
      </div>

      {/* ✅ Footer */}
      <footer className="bg-[#e2e8f0] text-[#334155] text-sm text-center py-6 mt-20 rounded-t-xl shadow-inner">
        <p>© {new Date().getFullYear()} Talentraxion. All rights reserved.</p>
        <p className="mt-1">Built with empathy. Powered by AI. Designed for growth.</p>
      </footer>
    </div>
  );
}
